import { useCallback, useEffect, useRef, useState } from 'react';
import {
  fetchSponsoredPlacement,
  newPageViewId,
  recordSponsoredEvent,
  rememberSponsoredOfferReference,
  type CommercialSurface,
  type SponsoredPlacement,
} from '../commercialSponsoredApi';
import { HopperIcon } from '../ui/icons/HopperIcon';
import './sponsored-placement.css';

type Props = {
  city: string;
  surface: CommercialSurface;
  suppressOfferIds?: ReadonlySet<string>;
  className?: string;
};

const VIEWABILITY_MS = 1000;
const TOKEN_REFRESH_MS = 9 * 60 * 1000 + 30 * 1000;

function clearTimer(timer: React.MutableRefObject<number | null>): void {
  if (timer.current !== null) {
    window.clearTimeout(timer.current);
    timer.current = null;
  }
}

export function SponsoredPlacementCard({ city, surface, suppressOfferIds, className = '' }: Props) {
  const [placement, setPlacement] = useState<SponsoredPlacement | null>(null);
  const [pageViewId, setPageViewId] = useState(() => newPageViewId());
  const rootRef = useRef<HTMLElement | null>(null);
  const visibleTimerRef = useRef<number | null>(null);
  const refreshTimerRef = useRef<number | null>(null);
  const intersectionRef = useRef(false);
  const visibleSentRef = useRef(false);
  const requestSequenceRef = useRef(0);

  const scheduleVisibility = useCallback((item: SponsoredPlacement | null) => {
    clearTimer(visibleTimerRef);
    if (!item || visibleSentRef.current || !intersectionRef.current || document.visibilityState !== 'visible') {
      return;
    }

    visibleTimerRef.current = window.setTimeout(() => {
      visibleTimerRef.current = null;
      if (!intersectionRef.current || document.visibilityState !== 'visible' || visibleSentRef.current) return;
      visibleSentRef.current = true;
      void recordSponsoredEvent(item.delivery_token, 'visible_impression').catch(() => undefined);
    }, VIEWABILITY_MS);
  }, []);

  const load = useCallback(async (logicalPageViewId: string) => {
    const requestId = ++requestSequenceRef.current;
    try {
      const response = await fetchSponsoredPlacement(city, surface, logicalPageViewId);
      if (requestId !== requestSequenceRef.current) return;
      const next = response.data.items[0] ?? null;
      setPlacement(next);
      visibleSentRef.current = false;
      clearTimer(refreshTimerRef);

      if (next) {
        const endAt = new Date(next.ends_at).getTime();
        const untilEnd = Number.isFinite(endAt) ? Math.max(0, endAt - Date.now()) : TOKEN_REFRESH_MS;
        const refreshIn = Math.min(TOKEN_REFRESH_MS, untilEnd);
        refreshTimerRef.current = window.setTimeout(() => {
          const freshPageViewId = newPageViewId();
          setPageViewId(freshPageViewId);
        }, Math.max(1000, refreshIn));
      }
    } catch {
      if (requestId === requestSequenceRef.current) {
        setPlacement(null);
        clearTimer(refreshTimerRef);
      }
    }
  }, [city, surface]);

  useEffect(() => {
    void load(pageViewId);
  }, [load, pageViewId]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root || !placement) return undefined;

    const observer = new IntersectionObserver((entries) => {
      const entry = entries[0];
      intersectionRef.current = Boolean(entry && entry.isIntersecting && entry.intersectionRatio >= 0.5);
      scheduleVisibility(placement);
      if (!intersectionRef.current) clearTimer(visibleTimerRef);
    }, { threshold: [0, 0.5, 1] });

    observer.observe(root);
    return () => {
      observer.disconnect();
      intersectionRef.current = false;
      clearTimer(visibleTimerRef);
    };
  }, [placement, scheduleVisibility]);

  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState !== 'visible') {
        clearTimer(visibleTimerRef);
        return;
      }
      scheduleVisibility(placement);
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [placement, scheduleVisibility]);

  useEffect(() => () => {
    requestSequenceRef.current += 1;
    clearTimer(visibleTimerRef);
    clearTimer(refreshTimerRef);
  }, []);

  if (!placement) return null;
  if (placement.subject.type === 'offer' && suppressOfferIds?.has(placement.subject.id)) return null;

  const open = () => {
    if (placement.subject.type === 'offer') {
      rememberSponsoredOfferReference(placement.subject.id, placement.delivery_token);
    }
    void recordSponsoredEvent(placement.delivery_token, 'open').catch(() => undefined);
  };

  const openPlacement = () => {
    open();
    window.location.assign(placement.subject.destination);
  };

  const advertisement = placement.advertisement;
  const isNeutralOffer = !advertisement && placement.subject.type === 'offer';

  if (isNeutralOffer) {
    return (
      <article
        ref={(node) => { rootRef.current = node; }}
        className={`sponsored-placement-card sponsored-placement-card--neutral-offer sponsored-placement-card--${surface} ${className}`.trim()}
        data-commercial-surface={surface}
        aria-label={placement.title}
        role="link"
        tabIndex={0}
        onClick={(event) => {
          if ((event.target as HTMLElement).closest('button, a')) return;
          openPlacement();
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            openPlacement();
          }
        }}
      >
        <div className="sponsored-placement-card__body">
          <div className="sponsored-placement-card__neutral-head">
            <span className="sponsored-placement-card__offer-kind">Предложение</span>
            <span className="sponsored-placement-card__neutral-venue">{placement.venue.name ?? 'Заведение Хоппера'}</span>
          </div>
          <h2>{placement.title}</h2>
          {placement.description ? <p className="sponsored-placement-card__description" title={placement.description}>{placement.description}</p> : null}
          <div className="sponsored-placement-card__neutral-footer">
            <HopperIcon name="pin" size={16} />
            <span>{placement.venue.name ?? 'Заведение Хоппера'}</span>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article
      ref={(node) => { rootRef.current = node; }}
      className={`sponsored-placement-card sponsored-placement-card--${surface} ${className}`.trim()}
      data-commercial-surface={surface}
      aria-label={advertisement ? `Реклама: ${placement.title}` : placement.title}
    >
      {placement.media?.thumbnail_url || placement.media?.url ? (
        <img
          className="sponsored-placement-card__media"
          src={placement.media.thumbnail_url ?? placement.media.url}
          alt=""
        />
      ) : null}
      <div className="sponsored-placement-card__body">
        {advertisement ? (
          <div className="sponsored-placement-card__adline">
            <strong>{advertisement.label}</strong>
            <span>{advertisement.advertiser_name}</span>
          </div>
        ) : null}
        <div className="sponsored-placement-card__venue">
          <HopperIcon name={placement.subject.type === 'event' ? 'calendar' : 'gift'} size={18} />
          <span>{placement.venue.name ?? 'Заведение Хоппера'}</span>
        </div>
        <h2>{placement.title}</h2>
        <p>{placement.description}</p>
        {advertisement ? (
          <div className="sponsored-placement-card__legal">
            <span>ИНН {advertisement.advertiser_inn}</span>
            <span>erid: {advertisement.erid}</span>
          </div>
        ) : null}
        <a
          className="sponsored-placement-card__cta"
          href={placement.subject.destination}
          onClick={open}
        >
          {placement.subject.type === 'event' ? 'Открыть событие' : 'Открыть'}
        </a>
      </div>
    </article>
  );
}
